import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Plus, RotateCw, 
  PanelTop, ThermometerSnowflake, Building, Grape 
} from 'lucide-react';

// Import custom components
import ConnectionStatus from '../components/mqtt/ConnectionStatus';
import TankFormModal from '../components/Modals/TankFormModal';
import DeviceFormModal from '../components/Modals/DeviceFormModal';

// Import tab components
import WineryTab from '../components/mqtt/WineryTab';
import ClimateTab from '../components/mqtt/ClimateTab';
import FacilityTab from '../components/mqtt/FacilityTab';
import VineyardTab from '../components/mqtt/VineyardTab';

const MqttDashboard = () => {
  // State for active tab and data
  const [activeTab, setActiveTab] = useState('winery');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // MQTT and connection states
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [mqttStatus, setMqttStatus] = useState({ 
    status: 'unknown', 
    message: 'Checking connection...' 
  });

  // Data states
  const [tanks, setTanks] = useState([]);
  const [climateDevices, setClimateDevices] = useState([]);
  const [facilityDevices, setFacilityDevices] = useState([]);
  const [vineyardDevices, setVineyardDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);

  // Modal states
  const [showTankForm, setShowTankForm] = useState(false);
  const [showDeviceForm, setShowDeviceForm] = useState(false);
  const [editingTank, setEditingTank] = useState(null);
  const [editingDevice, setEditingDevice] = useState(null);

  // Refresh tracking
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Vineyard data (demo/placeholder)
  const [vineyardData, setVineyardData] = useState({
    rainfall: [],
    soilMoisture: [],
    temperature: []
  });

  // Fetch MQTT connection status
  const fetchMqttStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/mqtt/status');
      setIsConnected(response.data.connected);
      setMqttStatus({
        status: response.data.connected ? 'connected' : 'disconnected',
        message: response.data.message || ''
      });
      
      if (response.data.lastMessage) {
        setLastMessage(response.data.lastMessage);
      }
    } catch (error) {
      console.error('Error fetching MQTT status:', error);
      setIsConnected(false);
      setMqttStatus({
        status: 'error',
        message: 'Failed to check MQTT connection'
      });
    }
  }, []);

  // Fetch tanks
  const fetchTanks = useCallback(async () => {
    try {
      const response = await axios.get('/api/tanks');
      setTanks(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching tanks:', error);
      setError('Failed to load tanks');
    }
  }, []);

  // Fetch device types
  const fetchDeviceTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/device-types');
      setDeviceTypes(response.data);
    } catch (error) {
      console.error('Error fetching device types:', error);
      setError('Failed to load device types');
    }
  }, []);

  // Fetch climate devices
  const fetchClimateDevices = useCallback(async () => {
    try {
      const response = await axios.get('/api/devices?category=climate');
      setClimateDevices(response.data);
    } catch (error) {
      console.error('Error fetching climate devices:', error);
      setError('Failed to load climate devices');
    }
  }, []);

  // Fetch facility devices
  const fetchFacilityDevices = useCallback(async () => {
    try {
      const response = await axios.get('/api/devices?category=facility');
      setFacilityDevices(response.data);
    } catch (error) {
      console.error('Error fetching facility devices:', error);
      setError('Failed to load facility devices');
    }
  }, []);

  // Fetch vineyard devices
  const fetchVineyardDevices = useCallback(async () => {
    try {
      const response = await axios.get('/api/devices?category=vineyard');
      setVineyardDevices(response.data);
      
      // For demo, generate sample vineyard data
      generateVineyardData();
    } catch (error) {
      console.error('Error fetching vineyard devices:', error);
      setError('Failed to load vineyard devices');
    }
  }, []);

  // Generate demo vineyard data
  const generateVineyardData = () => {
    const now = new Date();
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (29 - i));
      return date.toISOString().split('T')[0];
    });

    // Generate random data
    const rainfall = last30Days.map(date => ({
      date,
      value: Math.round(Math.random() * 20) / 10,
    }));

    const soilMoisture = last30Days.map(date => ({
      date,
      value: Math.round(60 + Math.random() * 30),
    }));

    const temperature = last30Days.map(date => ({
      date,
      min: Math.round(40 + Math.random() * 20),
      max: Math.round(70 + Math.random() * 15),
      avg: Math.round(55 + Math.random() * 15),
    }));

    setVineyardData({ rainfall, soilMoisture, temperature });
  };

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch shared data
        await Promise.all([
          fetchMqttStatus(),
          fetchDeviceTypes()
        ]);
        
        // Fetch tab-specific data
        switch(activeTab) {
          case 'winery':
            await fetchTanks();
            break;
          case 'climate':
            await fetchClimateDevices();
            break;
          case 'facility':
            await fetchFacilityDevices();
            break;
          case 'vineyard':
            await fetchVineyardDevices();
            break;
          default:
            break;
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [
    activeTab, 
    fetchTanks, 
    fetchClimateDevices, 
    fetchFacilityDevices, 
    fetchVineyardDevices,
    fetchMqttStatus,
    fetchDeviceTypes
  ]);

  // Setup refresh intervals
  useEffect(() => {
    const intervalId = setInterval(() => {
      switch(activeTab) {
        case 'winery':
          fetchTanks();
          break;
        case 'climate':
          fetchClimateDevices();
          break;
        case 'facility':
          fetchFacilityDevices();
          break;
        case 'vineyard':
          fetchVineyardDevices();
          break;
      }
      fetchMqttStatus();
    }, 30000); // Refresh every 30 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [
    activeTab,
    fetchTanks,
    fetchClimateDevices,
    fetchFacilityDevices,
    fetchVineyardDevices,
    fetchMqttStatus
  ]);

  // Tank-related handlers
  const handleSaveTank = async (tankData) => {
    try {
      if (tankData.tankID) {
        await axios.put(`/api/tanks/${tankData.tankID}`, tankData);
      } else {
        await axios.post('/api/tanks', tankData);
      }
      
      await fetchTanks();
      setShowTankForm(false);
      setEditingTank(null);
    } catch (error) {
      console.error('Error saving tank:', error);
      setError('Failed to save tank');
    }
  };

  const handleDeleteTank = async (tankId) => {
    if (window.confirm('Are you sure you want to delete this tank?')) {
      try {
        await axios.delete(`/api/tanks/${tankId}`);
        await fetchTanks();
      } catch (error) {
        console.error('Error deleting tank:', error);
        setError('Failed to delete tank');
      }
    }
  };

  // Device-related handlers
  const handleSaveDevice = async (deviceData) => {
    try {
      // For new devices, the deviceID will be undefined/null
      if (deviceData.deviceID) {
        // Update existing device - make sure deviceID is numeric
        await axios.put(`/api/devices/${deviceData.deviceID}`, deviceData);
      } else {
        // Add new device - POST to collection endpoint
        await axios.post('/api/devices', deviceData);
      }
      
      // Refresh devices based on current tab
      switch(activeTab) {
        case 'climate':
          await fetchClimateDevices();
          break;
        case 'facility':
          await fetchFacilityDevices();
          break;
        case 'vineyard':
          await fetchVineyardDevices();
          break;
      }
      
      setShowDeviceForm(false);
      setEditingDevice(null);
    } catch (error) {
      console.error('Error saving device:', error);
      setError('Failed to save device');
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        await axios.delete(`/api/devices/${deviceId}`);
        
        // Refresh devices based on current tab
        switch(activeTab) {
          case 'climate':
            await fetchClimateDevices();
            break;
          case 'facility':
            await fetchFacilityDevices();
            break;
          case 'vineyard':
            await fetchVineyardDevices();
            break;
        }
      } catch (error) {
        console.error('Error deleting device:', error);
        setError('Failed to delete device');
      }
    }
  };

  // Device toggle handler
  const handleToggleDevice = async (deviceId, state) => {
    try {
      await axios.put(`/api/devices/${deviceId}/toggle`, { state });
      
      // Refresh devices after short delay
      setTimeout(async () => {
        switch(activeTab) {
          case 'climate':
            await fetchClimateDevices();
            break;
          case 'facility':
            await fetchFacilityDevices();
            break;
          case 'vineyard':
            await fetchVineyardDevices();
            break;
        }
      }, 1000);
    } catch (error) {
      console.error('Error toggling device:', error);
      setError('Failed to toggle device');
    }
  };

  // Tank temperature and jacket handlers
  const handleToggleJacket = async (tankId, state) => {
    try {
      await axios.put(`/api/tanks/${tankId}/jacket`, { state });
      
      // Optimistically update UI
      setTanks(tanks.map(tank => 
        tank.tankID === tankId ? { ...tank, jacketOn: state } : tank
      ));
      
      // Refresh to get actual state
      setTimeout(fetchTanks, 2000);
    } catch (error) {
      console.error('Error toggling jacket:', error);
      setError('Failed to toggle cooling jacket');
      
      // Revert optimistic update
      fetchTanks();
    }
  };

  const handleSetTemperature = async (tankId, temperature) => {
    try {
      await axios.put(`/api/tanks/${tankId}/temperature`, { temperature });
      
      // Optimistically update UI
      setTanks(tanks.map(tank => 
        tank.tankID === tankId ? { ...tank, targetTemp: temperature } : tank
      ));
      
      // Refresh to get actual state
      setTimeout(fetchTanks, 2000);
    } catch (error) {
      console.error('Error setting temperature:', error);
      setError('Failed to set temperature');
      
      // Revert optimistic update
      fetchTanks();
    }
  };

  // Manual refresh handler
  const handleManualRefresh = () => {
    switch(activeTab) {
      case 'winery':
        fetchTanks();
        break;
      case 'climate':
        fetchClimateDevices();
        break;
      case 'facility':
        fetchFacilityDevices();
        break;
      case 'vineyard':
        fetchVineyardDevices();
        break;
    }
    fetchMqttStatus();
  };

  // Render tabs based on active selection
  const renderActiveTab = () => {
    switch(activeTab) {
      case 'winery':
        return (
          <WineryTab
            tanks={tanks}
            onAddTank={() => {
              setEditingTank(null);
              setShowTankForm(true);
            }}
            onEditTank={(tank) => {
              setEditingTank(tank);
              setShowTankForm(true);
            }}
            onDeleteTank={handleDeleteTank}
            onToggleJacket={handleToggleJacket}
            onSetTemperature={handleSetTemperature}
            onRefresh={handleManualRefresh}
            lastRefresh={lastRefresh}
          />
        );
      case 'climate':
        return (
          <ClimateTab
            devices={climateDevices}
            deviceTypes={deviceTypes}
            onAddDevice={() => {
              setEditingDevice(null);
              setShowDeviceForm(true);
            }}
            onEditDevice={(device) => {
              setEditingDevice(device);
              setShowDeviceForm(true);
            }}
            onDeleteDevice={handleDeleteDevice}
            onToggleDevice={handleToggleDevice}
            onRefresh={handleManualRefresh}
            lastRefresh={lastRefresh}
          />
        );
      case 'facility':
        return (
          <FacilityTab
            devices={facilityDevices}
            deviceTypes={deviceTypes}
            onAddDevice={() => {
              setEditingDevice(null);
              setShowDeviceForm(true);
            }}
            onEditDevice={(device) => {
              setEditingDevice(device);
              setShowDeviceForm(true);
            }}
            onDeleteDevice={handleDeleteDevice}
            onToggleDevice={handleToggleDevice}
            onRefresh={handleManualRefresh}
            lastRefresh={lastRefresh}
          />
        );
      case 'vineyard':
        return (
          <VineyardTab
            devices={vineyardDevices}
            deviceTypes={deviceTypes}
            vineyardData={vineyardData}
            onAddDevice={() => {
              setEditingDevice(null);
              setShowDeviceForm(true);
            }}
            onEditDevice={(device) => {
              setEditingDevice(device);
              setShowDeviceForm(true);
            }}
            onDeleteDevice={handleDeleteDevice}
            onToggleDevice={handleToggleDevice}
            onRefresh={handleManualRefresh}
            lastRefresh={lastRefresh}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Winery Monitoring Dashboard</h4>
            <ConnectionStatus 
              isConnected={isConnected} 
              lastMessage={lastMessage}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'winery' ? 'active' : ''}`}
            onClick={() => setActiveTab('winery')}
          >
            <PanelTop size={16} className="me-1" />
            Winery Tanks
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'climate' ? 'active' : ''}`}
            onClick={() => setActiveTab('climate')}
          >
            <ThermometerSnowflake size={16} className="me-1" />
            Climate Controls
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'facility' ? 'active' : ''}`}
            onClick={() => setActiveTab('facility')}
          >
            <Building size={16} className="me-1" />
            Facility
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'vineyard' ? 'active' : ''}`}
            onClick={() => setActiveTab('vineyard')}
          >
            <Grape size={16} className="me-1" />
            Vineyard
          </button>
        </li>
      </ul>

      {/* Render active tab content */}
      {renderActiveTab()}

      {/* Modals */}
      <TankFormModal
        show={showTankForm}
        onClose={() => {
          setShowTankForm(false);
          setEditingTank(null);
        }}
        onSave={handleSaveTank}
        tank={editingTank}
      />

      <DeviceFormModal
        show={showDeviceForm}
        onClose={() => {
          setShowDeviceForm(false);
          setEditingDevice(null);
        }}
        onSave={handleSaveDevice}
        device={editingDevice}
        deviceTypes={deviceTypes}
      />

      <style jsx>{`
        .btn svg {
          vertical-align: middle;
          margin-top: -2px;
        }
        .btn:hover svg {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
        .nav-link {
          display: flex;
          align-items: center;
        }
        .card {
          transition: all 0.2s ease;
        }
        .card:hover {
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }
      `}</style>
    </div>
  );
};

export default MqttDashboard;